import { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { CitationVehicle } from '@/components/citation/CitationVehicle';
import { SearchModal } from '@/components/modals/SearchModal';
import { PageModal } from '@/components/modals/PageModal';
import { NPSDVehicleScreen } from '@/components/npsd/NPSDVehicleScreen';
import { api } from '@/services/api-class';
import moment from 'moment';

interface CitationVehicleSectionProps {
  citation: any;
  isEditable: boolean;
  isIssuedFieldLocked: boolean;
  theme: string;
  onVehicleIdChange: (vehicleId: number | null) => void;
  onDirty: () => void;
}

export const CitationVehicleSection = memo(function CitationVehicleSection({
  citation,
  isEditable,
  isIssuedFieldLocked,
  theme,
  onVehicleIdChange,
  onDirty,
}: CitationVehicleSectionProps) {
  // Vehicle form state
  const [vehicleData, setVehicleData] = useState<any>({
    plateNumber: '', plateState: '', make: '', model: '',
    year: null, color: '', vin: '',
  });
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);

  // Search modal state
  const [showVehicleSearchModal, setShowVehicleSearchModal] = useState(false);

  // Linked vehicle view modal state
  const [showLinkedVehicleModal, setShowLinkedVehicleModal] = useState(false);
  const [linkedVehicleModalId, setLinkedVehicleModalId] = useState<number | null>(null);
  const [linkedVehicleAllowEdit, setLinkedVehicleAllowEdit] = useState(false);

  // Load vehicle data when citation has npsdVehicleId but no snapshot and we don't have it yet
  useEffect(() => {
    if (citation.npsdVehicleId && !citation.vehicleSnapshot && !selectedVehicle) {
      api.getNPSDItemById(String(citation.npsdVehicleId), 'vehicle', (result: any) => {
        const vehicle = new (window as any).Vehicle(result);
        setSelectedVehicle(vehicle);
        setVehicleData({
          plateNumber: vehicle.plateNumber || '',
          plateState: vehicle.plateState || '',
          make: vehicle.make || '',
          model: vehicle.model || '',
          year: vehicle.year || null,
          color: vehicle.color || '',
          vin: vehicle.vin || '',
        });
      });
    }
  }, [citation.npsdVehicleId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Computed: vehicle display items
  const getVehicleSource = useCallback((): any => {
    if ((citation.status === 'issued' || citation.status === 'voided') && citation.vehicleSnapshot) {
      return citation.vehicleSnapshot;
    }
    return selectedVehicle || vehicleData;
  }, [citation.status, citation.vehicleSnapshot, selectedVehicle, vehicleData]);

  const vehicleDisplayItems = useMemo(() => {
    const src = getVehicleSource();
    if (!src) return [];
    const plateInfo = src.plateNumber ? `${src.plateNumber} (${src.plateState || src.plateStateValue || '-'})` : '-';
    const vehicle = [src.year, src.make || src.makeValue, src.model || src.modelValue].filter(Boolean).join(' ') || '-';
    const color = src.color || src.colorValue || '-';
    const vin = src.vin || '-';
    return [
      { label: 'Plate Number', value: plateInfo },
      { label: 'Vehicle', value: vehicle },
      { label: 'Color', value: color },
      { label: 'VIN', value: vin },
    ];
  }, [getVehicleSource]);

  const vehicleSearchInitialValues = useMemo(() => ({
    plateNumber: vehicleData.plateNumber || '',
    plateState: vehicleData.plateState || '',
    vin: vehicleData.vin || '',
    make: vehicleData.make || '',
    model: vehicleData.model || '',
    color: vehicleData.color || '',
  }), [vehicleData]);

  // Handlers
  const setLinkedVehicleData = useCallback((vehicle: any) => {
    setSelectedVehicle(vehicle);
    onVehicleIdChange(vehicle.id);
    setVehicleData({
      plateNumber: vehicle.plateNumber || '',
      plateState: vehicle.plateState || '',
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year || null,
      color: vehicle.color || '',
      vin: vehicle.vin || '',
    });
    onDirty();
  }, [onVehicleIdChange, onDirty]);

  const handleVehicleChange = useCallback((data: any) => {
    setVehicleData(data);
    onDirty();
  }, [onDirty]);

  const handleLinkVehicle = useCallback(() => {
    setShowVehicleSearchModal(true);
  }, []);

  const handleCreateVehicle = useCallback((vehicleFormData: any) => {
    if (!vehicleFormData.plateNumber && !vehicleFormData.vin) {
      (window as any).App?.showPopupNotification?.('Plate number or VIN is required.', 'error');
      return;
    }
    const newVehicle: any = {
      plateNumber: vehicleFormData.plateNumber || null,
      plateState: vehicleFormData.plateState || null,
      make: vehicleFormData.make || null,
      model: vehicleFormData.model || null,
      year: vehicleFormData.year || null,
      color: vehicleFormData.color || null,
      vin: vehicleFormData.vin || null,
    };
    api.saveNPSDItem(newVehicle, 'vehicle', (result: any) => {
      (window as any).App?.showPopupNotification?.('Vehicle created and linked successfully.', 'success');
      const vehicle = new (window as any).Vehicle(result);
      setLinkedVehicleData(vehicle);
    });
  }, [setLinkedVehicleData]);

  const handleUnlinkVehicle = useCallback(() => {
    onVehicleIdChange(null);
    setSelectedVehicle(null);
    onDirty();
  }, [onVehicleIdChange, onDirty]);

  const handleClearVehicle = useCallback(() => {
    setSelectedVehicle(null);
    onVehicleIdChange(null);
    setVehicleData({
      plateNumber: '', plateState: '', make: '', model: '',
      year: null, color: '', vin: '',
    });
    onDirty();
  }, [onVehicleIdChange, onDirty]);

  const handleShowLinkedVehicle = useCallback(() => {
    if (citation.npsdVehicleId) {
      setLinkedVehicleModalId(citation.npsdVehicleId);
      setLinkedVehicleAllowEdit(citation.status === 'draft');
      setShowLinkedVehicleModal(true);
    }
  }, [citation.npsdVehicleId, citation.status]);

  const handleVehicleSearchSelect = useCallback((vehicleSearchData: any) => {
    setShowVehicleSearchModal(false);
    const vehicle = new (window as any).Vehicle(vehicleSearchData);
    setLinkedVehicleData(vehicle);
  }, [setLinkedVehicleData]);

  const handleVehicleSearchView = useCallback((vehicleSearchData: any) => {
    if (vehicleSearchData?.id) {
      setLinkedVehicleModalId(vehicleSearchData.id);
      setLinkedVehicleAllowEdit(false);
      setShowLinkedVehicleModal(true);
    }
  }, []);

  const handleVehicleUpdatedInModal = useCallback(() => {
    // Refetch by ID to guarantee full data — the API update result may be sparse.
    if (!citation.npsdVehicleId || citation.npsdVehicleId !== linkedVehicleModalId) return;
    api.getNPSDItemById(String(citation.npsdVehicleId), 'vehicle', (full: any) => {
      const vehicle = new (window as any).Vehicle(full);
      setLinkedVehicleData(vehicle);
    });
  }, [citation.npsdVehicleId, linkedVehicleModalId, setLinkedVehicleData]);

  const handleCloseLinkedVehicleModal = useCallback(() => {
    setShowLinkedVehicleModal(false);
    setLinkedVehicleModalId(null);
    setLinkedVehicleAllowEdit(false);
  }, []);

  return (
    <>
      <div className="section">
        <div className="section-header"><span className="section-title">Vehicle (Optional)</span></div>
        <div className="section-body">
          {(citation.status === 'issued' || citation.status === 'voided') && citation.vehicleSnapshot ? (
            <div className="display-panel">
              <div className="display-grid">
                {vehicleDisplayItems.map((item, i) => (
                  <div key={i} className="display-item">
                    <span className="display-label">{item.label}</span>
                    <span className={`display-value ${!item.value || item.value === '-' ? 'empty' : ''}`}>{item.value}</span>
                  </div>
                ))}
              </div>
              <div className="snapshot-notice">
                Showing information captured at time of issuance.
                {citation.npsdVehicleId && (
                  <>{' '}<a href="#" onClick={(e) => { e.preventDefault(); handleShowLinkedVehicle(); }}>View current vehicle record</a></>
                )}
              </div>
            </div>
          ) : (citation.status === 'issued' || citation.status === 'voided') && !citation.vehicleSnapshot ? (
            <div className="display-panel empty-state-panel">
              <div className="empty-state-message">No vehicle was linked to this citation.</div>
            </div>
          ) : (
            <CitationVehicle
              value={vehicleData}
              npsdVehicleId={citation.npsdVehicleId}
              isReadOnly={!isEditable}
              isLocked={isIssuedFieldLocked}
              onInput={handleVehicleChange}
              onChange={handleVehicleChange}
              onLinkVehicle={handleLinkVehicle}
              onCreateVehicle={handleCreateVehicle}
              onUnlinkVehicle={handleUnlinkVehicle}
              onShowLinkedVehicle={handleShowLinkedVehicle}
              onClear={handleClearVehicle}
            />
          )}
        </div>
      </div>

      {/* Search Modal */}
      <SearchModal
        open={showVehicleSearchModal}
        searchType={'vehicle' as 'vehicle'}
        title="Link to Vehicle"
        theme={theme}
        initialValues={vehicleSearchInitialValues}
        onSelect={handleVehicleSearchSelect}
        onView={handleVehicleSearchView}
        onCancel={() => setShowVehicleSearchModal(false)}
      />

      {/* Linked Vehicle View Modal */}
      <PageModal
        open={showLinkedVehicleModal}
        modalId="citationLinkedVehicle"
        title="Linked Vehicle"
        theme={theme}
        onClose={handleCloseLinkedVehicleModal}
      >
        {showLinkedVehicleModal && linkedVehicleModalId && (
          <NPSDVehicleScreen
            vehicleId={linkedVehicleModalId}
            isWindow={true}
            onClose={handleCloseLinkedVehicleModal}
            onUpdated={handleVehicleUpdatedInModal}
            onSaved={handleVehicleUpdatedInModal}
          />
        )}
      </PageModal>
    </>
  );
});
